/**
 * Static plan data used as fallback when the API is unreachable
 * (e.g. during react-snap pre-rendering).
 *
 * Keep in sync with SubscriptionPlan.PLANS in
 * mostlyagent/api/services/subscription_service.py
 */
import { PlanConfig } from '../services/subscriptionService';

export const STATIC_HOSTING_CREDIT_RATES = {
  cpu: 0.3063,
  ram: 0.0768,
  disk: 0.000807,
} as const;

const SHARED_FEATURES: string[] = [
  'Tech Lead Agent',
  'Frontend Agent',
  'Backend Agent',
  'System Architect Agent',
  'MicroService Architecture',
  'Docker Agent',
  'Designer Agent',
  'Unittest Agent',
  'Integration Testing Agent',
  'Visual Testing Agent',
  'Kubernetes Cluster Stage Deployment',
  'Kubernetes Cluster Prod Deployment',
  'Realtime Visual Replay',
  'Supporting Any Programming Language',
  'Direct K8s Cluster Dashboard Access',
  'VSCode Editor',
  'Auto Scaling (Horizontal)',
  'Mobile Phone Interface Support',
  'Assign Tasks to Human',
  'Automatic Retry Agent',
  'Self-correcting agent loops',
  'Custom Agent',
];

export const STATIC_PLANS: Record<string, PlanConfig> = {
  free: {
    name: 'Free',
    monthly_price: 0,
    annual_price: 0,
    monthly_credits: 10,
    daily_drip_credits: 0,
    free_credit_schedule: {
      upfront: 10, boost_days: 10, boost_daily: 10, daily: 1, cycle_days: 31, estimated_total: 131,
      expires_each_cycle: true,
    },
    max_users: 1,
    topup_price_per_credit: 0.34,
    topup_price_per_credit_inr: 29.0,
    topup_price_per_credit_apple: 0.50,
    features: [...SHARED_FEATURES],
  },
  standard: {
    name: 'Standard',
    monthly_price: 4.49,
    annual_price: 48.49,
    monthly_credits: 500,
    daily_drip_credits: 15,
    max_users: -1,
    topup_price_per_credit: 0.24,
    topup_price_per_credit_inr: 20.0,
    topup_price_per_credit_apple: 0.34,
    monthly_price_inr: 449,
    annual_price_inr: 4849,
    apple_product_id_monthly: 'com.app.ai8080.sub.standard.monthly.v1',
    apple_product_id_annual: 'com.app.ai8080.sub.standard.annual.v1',
    apple_price_monthly: 1.49,
    apple_price_annual: 15.49,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  standard_india: {
    name: 'Standard',
    monthly_price: 1.99,
    annual_price: 21.49,
    monthly_credits: 200,
    daily_drip_credits: 6,
    max_users: -1,
    topup_price_per_credit: 0.24,
    topup_price_per_credit_inr: 20.0,
    topup_price_per_credit_apple: 0.34,
    monthly_price_inr: 199,
    annual_price_inr: 2149,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  byok: {
    name: 'BYOK',
    monthly_price: 5,
    annual_price: 54,
    monthly_credits: -1,
    daily_drip_credits: 0,
    max_users: -1,
    topup_price_per_credit: 0.24,
    topup_price_per_credit_inr: 20.0,
    topup_price_per_credit_apple: 0.34,
    monthly_price_inr: 449,
    annual_price_inr: 4838,
    apple_product_id_monthly: 'com.app.ai8080.sub.byok.monthly',
    apple_product_id_annual: 'com.app.ai8080.sub.byok.annual',
    apple_price_monthly: 7.49,
    apple_price_annual: 79.99,
    features: [...SHARED_FEATURES, 'BYOK Support', 'Unlimited Prompts'],
  },
  plus: {
    name: 'Plus',
    monthly_price: 25,
    annual_price: 270,
    monthly_credits: 3500,
    daily_drip_credits: 105,
    max_users: -1,
    topup_price_per_credit: 0.24,
    topup_price_per_credit_inr: 20.0,
    topup_price_per_credit_apple: 0.34,
    monthly_price_inr: 2240,
    annual_price_inr: 24192,
    apple_product_id_monthly: 'com.app.ai8080.sub.plus.monthly.v1',
    apple_product_id_annual: 'com.app.ai8080.sub.plus.annual.v1',
    apple_price_monthly: 35.99,
    apple_price_annual: 389.99,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  pro: {
    name: 'Pro',
    monthly_price: 250,
    annual_price: 2700,
    monthly_credits: 25000,
    daily_drip_credits: 750,
    max_users: -1,
    topup_price_per_credit: 0.19,
    topup_price_per_credit_inr: 16.0,
    topup_price_per_credit_apple: 0.27,
    monthly_price_inr: 22400,
    annual_price_inr: 241920,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  unlimited: {
    name: 'Unlimited',
    monthly_price: 1499,
    annual_price: null,
    monthly_credits: -1,
    daily_drip_credits: 0,
    max_users: -1,
    topup_price_per_credit: null,
    topup_price_per_credit_inr: null,
    topup_price_per_credit_apple: null,
    monthly_price_inr: 140000,
    annual_price_inr: null,
    is_unlimited: true,
    topups_allowed: false,
    apple_available: false,
    organization_unlimited: true,
    features: [
      ...SHARED_FEATURES,
      'BYOK Support',
      'Unlimited Credits',
      'Unlimited Users',
      'Unlimited Projects',
    ],
  },
};
